/**
 * Tabs Container Component for Organized Content Navigation
 * 
 * A comprehensive tabbed interface container that provides sticky navigation,
 * smooth transitions, and responsive design for organizing complex content
 * into digestible, navigable sections.
 * 
 * Core Features:
 * - Sticky tab navigation with smart positioning
 * - Dynamic background color adaptation when sticky
 * - Responsive tab layout with flex wrapping
 * - Smooth transition animations
 * - Consistent content area sizing
 * 
 * Navigation Behavior:
 * - Sticky positioning relative to header height
 * - Background color changes for sticky state visibility
 * - Centered tab layout with consistent spacing
 * - Active tab highlighting and visual feedback
 * - Touch-friendly mobile interactions
 * 
 * Content Management:
 * - Flexible children content rendering
 * - Minimum height constraints for content area
 * - Responsive container sizing and constraints
 * - Clean content area separation from navigation
 * 
 * Visual Design:
 * - Wood-brown background theme consistency
 * - Professional tab button styling
 * - Border separation between nav and content
 * - Consistent spacing and typography
 * - Smooth color transition animations
 * 
 * Responsive Features:
 * - Mobile-first tab layout approach
 * - Flex-wrap for tab overflow handling
 * - Adaptive spacing for different screen sizes
 * - Touch-optimized interaction areas
 * 
 * State Management:
 * - Active tab state handling
 * - Sticky behavior state management
 * - Tab change event delegation
 * - Ref management for scroll positioning
 * 
 * Performance:
 * - Efficient rendering with proper refs
 * - Smooth transition animations
 * - Optimized sticky positioning calculations
 * - Minimal re-renders with stable references
 * 
 * Accessibility:
 * - Semantic navigation structure
 * - Proper tab role and aria attributes
 * - Keyboard navigation support
 * - Clear content organization and hierarchy
 * 
 * @param {string} activeTab - Currently active tab identifier
 * @param {function} handleTabChange - Tab selection change handler
 * @param {Array} tabsData - Array of tab configuration objects
 * @param {number} headerHeight - Header height for sticky positioning
 * @param {boolean} isTabsSticky - Whether tabs are in sticky state
 * @param {object} tabsRef - React ref for tabs navigation element
 * @param {object} tabContentRef - React ref for content area
 * @param {ReactNode} children - Tab content to render
 * 
 * @description Professional tabbed interface with sticky navigation and responsive design
 */

import { useRef } from 'react';
import TabButton from '../ui/TabButton';

const TabsContainer = ({ 
  activeTab, 
  handleTabChange, 
  tabsData, 
  headerHeight, 
  isTabsSticky, 
  tabsRef,
  tabContentRef,
  children,
  isMobile = false
}) => {
  return (
    <section className={isMobile ? "pt-4 bg-wood-brown/10" : "py-16 md:py-24 bg-wood-brown/10"}>
      {/* Tab Navigation */}
      <div 
        ref={tabsRef}
        className={`sticky z-10 ${
          isMobile 
            ? `transition-all duration-300 ease-in-out ${isTabsSticky ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`
            : `transition-colors duration-100 ${isTabsSticky ? 'bg-black' : ''}`
        }`}
        style={{ 
          top: isMobile ? '0px' : `${headerHeight}px`
        }}
      >
        {/* Responsive tabs container */}
        <div className={isMobile ? "px-2" : ""}>
          <div className={
            isMobile 
              ? `max-w-5xl mx-auto flex justify-start overflow-x-auto border-b border-wood-brown/30 mb-6 hide-scrollbar mobile-tabs-scroll ${isTabsSticky ? 'py-2 tabs-sticky-mobile' : 'py-0'}`
              : "max-w-3xl mx-auto flex flex-wrap justify-center border-b border-wood-brown/30 mb-12"
          }>
            {tabsData.map(tab => (
              <TabButton 
                key={tab.id}
                isActive={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
                label={tab.label}
                isSticky={isTabsSticky}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Tab Content */}
        <div ref={tabContentRef} className="tab-content min-h-[500px] max-w-5xl mx-auto">
          {children}
        </div>
      </div>
    </section>
  );
};

export default TabsContainer; 